import * as React from "react";
import { RefObject } from "react";
import Form from "../commons/components/form/Form";
import InputState from "../commons/model/form/InputState";
import TextAreaInput from "../commons/components/form/input/TextAreaInput";
import IconSelectionInput from "../commons/components/form/input/IconSelectionInput";
import { required } from "../commons/model/form/Validators";
import Satisfaction, { SatisfactionLevel } from "../commons/model/Satisfaction";
import { ExpandableFabButton } from "../commons/components/buttons/ExpandableFabButton";
import sfetch from "../commons/utils/super-fetch";
import Option from "../commons/model/Option";
import { createRoot } from "react-dom/client";

export interface SatisfactionFormProps {
  satisfaction: Satisfaction;
}

interface SatisfactionFormState {
  level?: SatisfactionLevel;
  displayForm: boolean;
  globalErrorMsg?: string;
  canCollapse: boolean;
}

export default class SatisfactionForm extends React.Component<
  SatisfactionFormProps,
  SatisfactionFormState
> {
  private readonly fabButtonRef: RefObject<ExpandableFabButton>;

  constructor(props: SatisfactionFormProps) {
    super(props);

    this.submit = this.submit.bind(this);
    this.state = {
      level: this.props.satisfaction.level,
      displayForm: false,
      canCollapse: true,
    };

    this.fabButtonRef = React.createRef();
  }

  render() {
    return (
      <ExpandableFabButton
        ref={this.fabButtonRef}
        canCollapse={this.state.canCollapse}
        className="satisfaction-survey no-mobile"
        icon={this.toFontIcon(this.state.level)}
        banner="ic_feedback"
        title={i18n["Form.Satisfaction.Title"]}
      >
        <Form
          submitLabel={i18n["Global.Send"]}
          submit={this.submit}
          onSubmitSuccess={() => this.fabButtonRef.current.collapse()}
          generalErrorMsg={this.state.globalErrorMsg}
        >
          <IconSelectionInput
            name="level"
            label={i18n["Form.Satisfaction.Level.Title"]}
            options={[
              new Option(SatisfactionLevel.GOOD, "ic_smiley_happy"),
              new Option(SatisfactionLevel.AVERAGE, "ic_smiley_neutral"),
              new Option(SatisfactionLevel.POOR, "ic_smiley_unhappy"),
            ]}
            initialValue={this.state.level}
            validators={[required()]}
          />
          <TextAreaInput
            name="comment"
            label={i18n["Form.Satisfaction.Message.Title"]}
            placeholder={i18n["Form.Satisfaction.Message.Placeholder"]}
            detail={i18n["Global.OptionalField"]}
            maxLength={500}
          />
        </Form>
      </ExpandableFabButton>
    );
  }

  private toFontIcon(level?: SatisfactionLevel): string {
    switch (level) {
      case SatisfactionLevel.GOOD: {
        return "ic_smiley_happy";
      }
      case SatisfactionLevel.AVERAGE: {
        return "ic_smiley_neutral";
      }
      case SatisfactionLevel.POOR: {
        return "ic_smiley_unhappy";
      }
      default: {
        return "ic_feedback";
      }
    }
  }

  private submit(form: { [key: string]: InputState }): Promise<void> {
    this.setState({
      globalErrorMsg: null,
      canCollapse: false,
    });
    return sfetch(this.props.satisfaction.updateEndpoint, {
      method: "PUT",
      body: JSON.stringify({
        level: form["level"].value,
        comment: form["comment"].value || undefined,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        this.setState({
          level: form["level"].value,
          canCollapse: true,
        });
      })
      .catch((response) => {
        this.setState({
          globalErrorMsg: i18n["Global.Status.technical_error"],
          canCollapse: true,
        });
        throw response;
      });
  }

  static loadComponent(elementId: string, props: SatisfactionFormProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<SatisfactionForm {...props} />);
  }
}
